import React, { useState, useCallback, useEffect } from 'react'

import { SearchService } from '../services/searchService'
import type { Gif } from '../types'
import type { ExplicitRating } from '../../../../functions/src/shared/types/gif'

import { SearchContext, type SearchContextType } from './SearchContext'

interface SearchState {
  results: Gif[]
  loading: boolean
  error: string | null
  query: string
  hasSearched: boolean
  totalHits: number
  suggestions: string[]
  loadingSuggestions: boolean
}

interface SearchProviderProps {
  children: React.ReactNode
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [state, setState] = useState<SearchState>({
    results: [],
    loading: false,
    error: null,
    query: '',
    hasSearched: false,
    totalHits: 0,
    suggestions: [],
    loadingSuggestions: false,
  })

  const search = useCallback(
    async (query: string, ratingFilter: ExplicitRating) => {
      if (!query.trim()) {
        setState((prev) => ({
          ...prev,
          results: [],
          query: '',
          hasSearched: false,
          totalHits: 0,
          error: null,
        }))
        return
      }

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        query,
      }))

      try {
        const searchResult = await SearchService.searchGifs(query, {
          ratingFilter,
        })
        setState((prev) => ({
          ...prev,
          results: searchResult.hits,
          totalHits: searchResult.nbHits,
          loading: false,
          hasSearched: true,
          error: null,
        }))
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Search failed',
          results: [],
          totalHits: 0,
          hasSearched: true,
        }))
      }
    },
    []
  )

  const clearSearch = useCallback(() => {
    setState({
      results: [],
      loading: false,
      error: null,
      query: '',
      hasSearched: false,
      totalHits: 0,
      suggestions: [],
      loadingSuggestions: false,
    })
  }, [])

  const getSuggestions = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setState((prev) => ({
        ...prev,
        suggestions: [],
        loadingSuggestions: false,
      }))
      return
    }

    setState((prev) => ({
      ...prev,
      loadingSuggestions: true,
    }))

    try {
      const suggestions = await SearchService.getSuggestions(query)
      setState((prev) => ({
        ...prev,
        suggestions,
        loadingSuggestions: false,
      }))
    } catch (error) {
      console.error('Error getting suggestions:', error)
      setState((prev) => ({
        ...prev,
        suggestions: [],
        loadingSuggestions: false,
      }))
    }
  }, [])

  const clearSuggestions = useCallback(() => {
    setState((prev) => ({
      ...prev,
      suggestions: [],
      loadingSuggestions: false,
    }))
  }, [])

  // Auto-clear suggestions when search is executed
  useEffect(() => {
    if (state.hasSearched && state.suggestions.length > 0) {
      clearSuggestions()
    }
  }, [state.hasSearched, state.suggestions.length, clearSuggestions])

  const contextValue: SearchContextType = {
    ...state,
    search,
    clearSearch,
    getSuggestions,
    clearSuggestions,
  }

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  )
}
